import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/**
 * Detect common in-app WebViews where cookies / auto-redirect may not work.
 * Returns the app name if detected, null otherwise.
 */
function detectWebView() {
  const ua = navigator.userAgent || "";
  if (/FBAN|FBAV/.test(ua)) return "Facebook";
  if (/Instagram/.test(ua)) return "Instagram";
  if (/GSA\//.test(ua)) return "Gmail";
  if (/Outlook-iOS|com\.microsoft\.Outlook|OWA\//.test(ua)) return "Outlook";
  if (/LinkedInApp/.test(ua)) return "LinkedIn";
  // Android generic WebView flag
  if (/; wv\)/.test(ua)) return "an in-app browser";
  // iOS in-app: has iPhone/iPad but lacks Safari and major standalone browsers
  if (
    /iPhone|iPad/.test(ua) &&
    !/Safari\//.test(ua) &&
    !/CriOS\//.test(ua) &&
    !/FxiOS\//.test(ua)
  ) {
    return "an in-app browser";
  }
  return null;
}

export default function VerifyEmailPage() {
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const [webView, setWebView] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [copied, setCopied] = useState(false);
  // Prevents React StrictMode from calling the verification API twice in development
  const hasVerifiedRef = useRef(false);

  const currentUrl = window.location.href;

  useEffect(() => {
    // StrictMode guard: only run once even if the effect fires twice in development
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;

    setWebView(detectWebView());

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setStatus("error");
      setMessage("No verification token found in this link. Please check your email for the correct link.");
      return;
    }

    axios
      .get(`${API}/auth/verify-email?token=${encodeURIComponent(token)}`, {
        withCredentials: false,
      })
      .then((res) => {
        const verifiedEmail = res.data?.email || "";
        setStatus("success");
        // Store email so the login page can pre-fill it
        if (verifiedEmail) {
          sessionStorage.setItem("arvay_verified_email", verifiedEmail);
        }
      })
      .catch((err) => {
        setStatus("error");
        setMessage(
          err.response?.data?.detail ||
            "Verification failed. The link may have expired or already been used."
        );
      });
  }, []);

  // Countdown redirect to sign-in page on success
  useEffect(() => {
    if (status !== "success") return;
    if (countdown <= 0) {
      window.location.href = "/?verified=true";
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: select and copy
      const el = document.createElement("input");
      el.value = currentUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FAFAF8] flex flex-col items-center justify-center px-4"
      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
    >
      {/* WebView warning banner */}
      {webView && (
        <div className="w-full max-w-sm mb-4 bg-amber-50 border border-amber-200 px-5 py-4 rounded-none">
          <p className="font-sans text-[11px] font-semibold text-amber-800 uppercase tracking-[0.12em] mb-1">
            Opening inside {webView}
          </p>
          <p className="font-sans text-xs text-amber-700 leading-relaxed mb-3">
            For the verification to work correctly, please open this link in your
            device&apos;s default browser (Safari / Chrome).
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyLink}
              data-testid="copy-link-btn"
              className="font-sans text-[10px] uppercase tracking-[0.15em] font-semibold bg-amber-800 text-white px-4 py-2 hover:bg-amber-900 transition-colors"
            >
              {copied ? "Copied!" : "Copy Link"}
            </button>
            <span className="font-sans text-[9px] text-amber-600">then paste in your browser</span>
          </div>
        </div>
      )}

      {/* Arvay wordmark */}
      <h1
        className="text-5xl tracking-[0.25em] uppercase font-light text-[#1A1A1A] mb-8"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        Arvay
      </h1>

      <div className="w-full max-w-sm bg-white border border-[#E8E8E4] shadow-sm p-8 text-center">
        {/* Loading */}
        {status === "loading" && (
          <>
            <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="font-sans text-sm text-neutral-500">Verifying your email…</p>
          </>
        )}

        {/* Success */}
        {status === "success" && (
          <>
            <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
              </svg>
            </div>
            <h2 className="text-xl font-light tracking-wider mb-2">Email Verified</h2>
            <p className="font-sans text-xs text-neutral-500 mb-1">
              Your account is now active.
            </p>
            <p className="font-sans text-xs text-neutral-400 mb-6">
              Redirecting to sign in in{" "}
              <span className="font-semibold text-black">{countdown}</span>
              {countdown === 1 ? " second" : " seconds"}…
            </p>
            <a
              href="/?verified=true"
              data-testid="go-to-signin-link"
              className="inline-block font-sans text-[10px] uppercase tracking-[0.18em] font-semibold bg-black text-white px-6 py-3 hover:bg-neutral-800 transition-colors"
            >
              Sign In Now
            </a>
          </>
        )}

        {/* Error */}
        {status === "error" && (
          <>
            <div className="w-10 h-10 border-2 border-black rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-light tracking-wider mb-2">Verification Failed</h2>
            <p className="font-sans text-xs text-neutral-500 mb-6">{message}</p>
            <a
              href="/"
              data-testid="back-to-signin-link"
              className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold underline text-neutral-600 hover:text-black transition-colors"
            >
              Back to Sign In
            </a>
          </>
        )}
      </div>

      <p className="font-sans text-[9px] text-neutral-300 mt-8 uppercase tracking-[0.15em]">
        &copy; {new Date().getFullYear()} Arvay. All rights reserved.
      </p>
    </div>
  );
}
